import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import { useGET_academicPeriodsSelectQuery } from 'redux/api/academicPeriodsApi'
import { useGET_groupSelectQuery } from 'redux/api/groupsApi'

import {
  useDELETE_timetableMutation,
  useGET_timetableByIdQuery,
  usePOST_timetableMutation,
  usePUT_timetableMutation,
  usePOST_timetableScheduleMutation,
  useDELETE_timetableScheduleMutation
} from 'redux/api/mySchoolApi'

import { ActionType, ITimetable } from 'types'
import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import SelectField from 'components/atoms/fields/SelectField'

import formModel, { initialValues, validationSchema } from 'utils/formModels/timetableModel'
import { crudColorMap, crudIconMap } from 'utils/helpers'


import useCrudData from 'utils/customHooks/useCrudData'
import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import SingleAttachmentButton from 'components/atoms/buttons/SingleAttachmentButton'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const TimetableForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug ='/timetables'
  const editSlug = `/timetables/edit/${id}`
  
  const [formSubmitCount, setFormSubmitCount] = useState(0)

  const [POST_timetableSchedule, { isLoading: timetableScheduleSaveLoading, error: timetableScheduleSaveError }] = usePOST_timetableScheduleMutation()
  const [DELETE_timetableSchedule, { isLoading: timetableScheduleDeleteLoading, error: timetableScheduleDeleteError }] = useDELETE_timetableScheduleMutation()

  useErrorAlert(timetableScheduleSaveError)
  
  const {
    isLoading,
    entity,
    handleSubmit,
  } = useCrudData({
    backToSlug,
    entityName: `${t('common.group-schedule')}`,
    entityId: id,
    getEntityQuery: useGET_timetableByIdQuery,
    createEntityMutation: usePOST_timetableMutation,
    editEntityMutation: usePUT_timetableMutation,
    deleteEntityMutation: useDELETE_timetableMutation
  })

  const {
    academic_period,
    group,
    schedule_file
  } = formModel()
  
  const serverValues = entity?.id
    ? {
        ...entity
      }
    : null

  return (
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: ITimetable = {
          ...values,
          academic_period_id: values.academic_period.id,
          group_id: values.group.id,
        }

        handleSubmit(action, id, data);
        setFormSubmitCount(formSubmitCount+1);
      }}
    >
      {
        ({ values, setFieldValue, errors }) => 
        <Form>
          <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
            <Grid item xs={12} md={6}>
              <SelectField
                  renderChildren={false}
                  fieldProps={{ ...academic_period }}
                  apiCall={useGET_academicPeriodsSelectQuery}
                  disabled={viewOnly}
                />
            </Grid>
            <Grid item xs={12} md={6}>
              <SelectField
                  renderChildren={false}
                  fieldProps={{ ...group }}
                  apiCall={useGET_groupSelectQuery}
                  disabled={viewOnly}
                />
            </Grid>
            <Grid item xs={12} md={6}>
              <SingleAttachmentButton
                  fieldProps={schedule_file}
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  id={entity?.id}
                  label={t('common.schedule-file')}
                  formSubmitCount={formSubmitCount}
                  savedAttachmentKey={'schedule_file'}
                  saveAttachmentApiCall={POST_timetableSchedule}
                  deleteAttachmentApiCall={DELETE_timetableSchedule}
                />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex' }} className="transparent-background">
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => navigate(backToSlug)}
                  sx={{ minHeight: '2.5rem', width: '10rem' }}
                >
                  <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                  {`${t('common.back')}`}
                </Button>
                {action === 'view' && 
                  <Box sx={{ marginLeft: 'auto' }}>
                    <Button
                      variant='outlined'
                      color='black'
                      onClick={() => navigate(editSlug)}
                      sx={{ minHeight: '2.5rem', width: '10rem' }}
                    >
                      <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                      {`${t('actions.edit')}`}
                    </Button>
                  </Box>
                }
                {action !== 'view' && (
                  <SubmitButton
                    color={crudColorMap[action]}
                    text={`${t('form-actions.' + action)}`}
                    icon={crudIconMap[action]}
                    sx={{ width: '10rem' }}
                    isLoading={isLoading}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Form>
      }
    </Formik>
  )
}
export default TimetableForm
